import React from 'react';
import Card from '@mui/joy/Card';
import CardActions from '@mui/joy/CardActions';
import Divider from '@mui/joy/Divider';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import { Button, Chip, Stack, Tooltip } from '@mui/joy';
import NumbersIcon from '@mui/icons-material/NumbersOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/Close';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LaunchIcon from '@mui/icons-material/Launch';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { CARD_STATES } from './constants';
import PostAddIcon from '@mui/icons-material/PostAdd';


export default function CourseVersionCard({
    item,
    cardMeta,
    wasSubmitted,
    alreadyAddedFlag,
    trainingProviders,
    isProcessing,
    isExpanded,
    onOpenExpanded,       // opens this card in expanded full-detail mode (click on card)
    onSubmitAndEnroll,    // expands + immediately starts enrollment (click on Enroll button)
    onLaunchTraining,     // opens training URL
    onReExpand,           // re-opens success panel for already-submitted
}) {
    const versionId = item.crsVersionId ?? `${item.crsId}_${item.versionNo}`;
    const cardState = cardMeta?.state ?? CARD_STATES.IDLE;
    const processing = isProcessing || cardState !== CARD_STATES.IDLE;

    const borderColor =
        cardState === CARD_STATES.LOADING ? '#1976d2'
            : cardState === CARD_STATES.SUCCESS ? '#43a047'
                : cardState === CARD_STATES.ERROR ? '#e53935'
                    : '#016799';

    return (
        <Card
            size="md"
            variant="outlined"
            color="primary"
            sx={{
                width: 450,
                minWidth: 450,
                minHeight: 400,
                flexShrink: 0,
                p: 0,
                gap: 0,
                borderRadius: 'md',
                border: `1px solid ${borderColor}`,
                opacity: isExpanded ? 0.4 : 1,
                filter: isExpanded ? 'blur(1px)' : 'none',
                transition: 'opacity 300ms ease, filter 300ms ease, border-color 400ms ease',
                animation: cardState === CARD_STATES.LOADING ? 'cvdPulse 1.2s ease-in-out infinite' : 'none',
                '&:hover': isExpanded ? {} : { boxShadow: 'md', bgcolor: 'warning.dark', variant: 'soft' },
            }}
        >
            <Box sx={{
                minWidth: 440, maxWidth: 440,
                p: 2, display: 'flex', flexDirection: 'column',
                bgcolor: 'background.surface', borderRadius: 'md',
            }}>
                {/* Top action row */}
                <Stack spacing={1} direction="row" useFlexGap sx={{
                    justifyContent: 'space-between', alignItems: 'flex-start', minHeight: 30,
                }}>
                    <ListItem>
                        {item.equivalentCrsStatus === 'Expired'
                            ? <Chip color="danger" size="md" variant="soft"><CloseIcon sx={{ height: '0.6em' }} />Expired&nbsp;&nbsp;</Chip>
                            : item.equivalentCrsStatus === 'Validated'
                                ? <Chip color="success" size="md" variant="soft"><CheckRoundedIcon sx={{ height: '0.6em' }} />Validated</Chip>
                                : null}
                    </ListItem>
                    <ListItem>
                        {!item.direct_registration
                            ? <Tooltip variant="outlined" arrow size="lg" title="Not eligible for direct registration">
                                <IconButton color="danger" aria-label="not-eligible"><DoNotDisturbIcon /></IconButton>
                              </Tooltip>
                            : alreadyAddedFlag || wasSubmitted
                                ? <Tooltip variant="outlined" arrow size="lg" title="Already Submitted">
                                    <IconButton color="success" aria-label="already-submitted"><AddTaskIcon /></IconButton>
                                  </Tooltip>
                                : <Tooltip variant="outlined" arrow size="lg" title="Enroll Now">
                                    <IconButton color="primary" variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); onSubmitAndEnroll?.(versionId); }}
                                        aria-label="submit-course">
                                        <PostAddIcon/>
                                    </IconButton>
                                  </Tooltip>
                        }
                    </ListItem>
                </Stack>

                {item.direct_registration
                    ? <Tooltip title="Eligible for Direct Registration" arrow color="neutral" placement="top-start" size="md" variant="solid">
                        <Typography level="h3" sx={{ color: '#016799' }}>
                            {item.tp_course_code}
                            <VerifiedIcon color={item?.equivalentCrsStatus === 'Validated' ? 'success' : 'primary'} style={{ marginLeft: 3, paddingBottom: 4 }} />
                        </Typography>
                      </Tooltip>
                    : <Tooltip title="Not Eligible for Direct Registration" arrow color="neutral" placement="top-start" size="md" variant="solid">
                        <Typography level="h3" sx={{ color: '#016799' }}>{item.tp_course_code}</Typography>
                      </Tooltip>
                }

                
                <Typography level="title-md" sx={{ cursor: 'pointer', color: '#016799', py: 1 }} 
                    onClick={(e) => { item.training_url && e.stopPropagation(); window.open(item.training_url, '_blank'); }}
                    startDecorator={item.training_url && <OpenInNewOutlinedIcon style={{ fontSize: '1.2rem' }} />}>
                    {item.title}
                </Typography>
                        
                <Typography level="body-xs" sx={{ minHeight: 180, py:1, height: 220 }}>{item.desc}</Typography>

                <Divider inset="none" />

                <List size="sm" sx={{ mx: 'calc(-1 * var(--ListItem-paddingX))' }}>
                    <ListItem>
                        <ListItemDecorator><Tooltip title="Version Number" size="sm"><NumbersIcon sx={{ fontSize: 20 }} /></Tooltip></ListItemDecorator>
                        {item.versionNo}
                    </ListItem>
                    <ListItem>
                        <ListItemDecorator><Tooltip title="Training Duration" size="sm"><TimerOutlinedIcon sx={{ fontSize: 20 }} /></Tooltip></ListItemDecorator>
                        {item.trainDuration}
                    </ListItem>
                    <ListItem>
                        <ListItemDecorator><Tooltip title="Training Provider" size="sm"><CorporateFareOutlinedIcon sx={{ fontSize: 20 }} /></Tooltip></ListItemDecorator>
                        {trainingProviders?.[parseInt(item.orgIssued)]}
                    </ListItem>
                </List>

                <Divider inset="none" />

                <CardActions sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', my: 1 }}>
                    <Typography level="body-sm" sx={{ mr: 'auto', ml: 1 }}>
                        {item?.completion_date
                            ? <><b>Last Register</b>{' - '}<Typography textColor="text.tertiary" sx={{ fontSize: 'sm' }}><b>{item.completion_date}</b></Typography></>
                            : <>Last Register{' - '}<Typography textColor="text.tertiary" sx={{ fontSize: 'sm' }}>N/A</Typography></>}
                    </Typography>

                    {wasSubmitted && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {item.training_url && (
                                <Tooltip title="Attempt Course" size="sm">
                                    <IconButton size="sm" color="success" variant="soft"
                                        onClick={(e) => { e.stopPropagation(); onLaunchTraining?.(item.training_url); }}
                                        aria-label="attempt-course">
                                        <LaunchIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="View result" size="sm">
                                <IconButton size="sm" color="success" variant="soft"
                                    onClick={(e) => { e.stopPropagation(); onReExpand?.(versionId); }}
                                    aria-label="re-expand">
                                    <ChevronRightOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </CardActions>
            </Box>
        </Card>
    );
}